<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 overflow-y-auto"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true"
  >
    <!-- Background overlay -->
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
        <div class="sm:flex sm:items-start">
          <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
            <!-- Conflict icon -->
            <svg class="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
            <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
              Data Conflict Detected
            </h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500">
                The {{ conflictData?.type }} you're editing has been modified by another user. 
                Please choose how to resolve this conflict.
              </p>
            </div>

            <!-- Conflict details -->
            <div v-if="conflictData" class="mt-4 space-y-4">
              <div class="bg-gray-50 rounded-lg p-4">
                <h4 class="text-sm font-medium text-gray-900 mb-3">Conflicted Fields:</h4>
                <div class="space-y-3">
                  <div
                    v-for="field in conflictData.conflictedFields"
                    :key="field"
                    class="border rounded-lg p-3 bg-white"
                  >
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-medium text-gray-700 capitalize">{{ formatFieldName(field) }}</span>
                      <select
                        v-model="fieldResolutions[field]"
                        class="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="local">Keep My Version</option>
                        <option value="remote">Use Their Version</option>
                      </select>
                    </div>
                    <div class="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div class="text-xs text-gray-500 mb-1">Your Version:</div>
                        <div class="p-2 bg-blue-50 rounded border">
                          {{ formatFieldValue(conflictData.localVersion[field]) }}
                        </div>
                      </div>
                      <div>
                        <div class="text-xs text-gray-500 mb-1">Their Version:</div>
                        <div class="p-2 bg-green-50 rounded border">
                          {{ formatFieldValue(conflictData.remoteVersion[field]) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Preview merged result -->
              <div class="bg-blue-50 rounded-lg p-4">
                <h4 class="text-sm font-medium text-gray-900 mb-2">Preview of Merged Result:</h4>
                <div class="text-sm text-gray-700">
                  <div v-for="field in conflictData.conflictedFields" :key="field" class="mb-1">
                    <span class="font-medium">{{ formatFieldName(field) }}:</span>
                    <span class="ml-2">
                      {{ formatFieldValue(getMergedFieldValue(field)) }}
                    </span>
                    <span 
                      :class="[
                        'ml-2 px-2 py-1 rounded text-xs',
                        fieldResolutions[field] === 'local' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      ]"
                    >
                      {{ fieldResolutions[field] === 'local' ? 'Your version' : 'Their version' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            @click="resolveConflict"
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Apply Resolution
          </button>
          <button
            @click="useLocalVersion"
            type="button"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
          >
            Keep All My Changes
          </button>
          <button
            @click="useRemoteVersion"
            type="button"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
          >
            Use All Their Changes
          </button>
          <button
            @click="dismissConflict"
            type="button"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { ConflictData, ConflictResolution } from '../services/ConflictResolutionService'
import { conflictResolutionService } from '../services/ConflictResolutionService'

interface Props {
  isOpen: boolean
  conflictData: ConflictData | null
}

interface Emits {
  (e: 'close'): void
  (e: 'resolved', resolution: ConflictResolution): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const fieldResolutions = ref<Record<string, 'local' | 'remote'>>({})

// Initialize field resolutions when conflict data changes
watch(() => props.conflictData, (newConflictData) => {
  if (newConflictData) {
    const resolutions: Record<string, 'local' | 'remote'> = {}
    newConflictData.conflictedFields.forEach(field => {
      resolutions[field] = 'local' // Default to local version
    })
    fieldResolutions.value = resolutions
  }
}, { immediate: true })

const formatFieldName = (field: string): string => {
  return field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
}

const formatFieldValue = (value: any): string => {
  if (value === null || value === undefined) return 'Not set'
  if (value instanceof Date) return value.toLocaleString()
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

const getMergedFieldValue = (field: string): any => {
  if (!props.conflictData) return ''
  
  const resolution = fieldResolutions.value[field]
  return resolution === 'local' 
    ? props.conflictData.localVersion[field]
    : props.conflictData.remoteVersion[field]
}

const resolveConflict = () => {
  if (!props.conflictData) return

  const mergedData = conflictResolutionService.createMergedData(
    props.conflictData.localVersion,
    props.conflictData.remoteVersion,
    fieldResolutions.value
  )

  const resolution: ConflictResolution = {
    conflictId: props.conflictData.id,
    resolution: 'merge',
    mergedData
  }

  emit('resolved', resolution)
  emit('close')
}

const useLocalVersion = () => {
  if (!props.conflictData) return

  const resolution: ConflictResolution = {
    conflictId: props.conflictData.id,
    resolution: 'local'
  }

  emit('resolved', resolution)
  emit('close')
}

const useRemoteVersion = () => {
  if (!props.conflictData) return

  const resolution: ConflictResolution = {
    conflictId: props.conflictData.id,
    resolution: 'remote'
  }

  emit('resolved', resolution)
  emit('close')
}

const dismissConflict = () => {
  if (props.conflictData) {
    conflictResolutionService.dismissConflict(props.conflictData.id)
  }
  emit('close')
}
</script>